import React, {useContext} from "react";
import {useEffect, useState} from "react";
import {changePageSizeState} from "../../../Slices/PageSlice";
import PageSizeState from "../../../Constants/Base/PageSizeState";
import {useDispatch} from "react-redux";
import TablePage from "../../../Components/DataGrid/TablePage";
import {
    formatColumnConstant,
    formatColumnDate,
    formatColumnObject,
} from "../../../Components/DataGrid/ValueFormatter";
import {changeBreadcrumbs} from "../../../Slices/BreadcrumbsSlice";
import Modules from "../../../Constants/Base/Modules";
import TablePageContext, {
    FilterDefaults,
    TableDataDefaults,
    TablePageOptionDefaults
} from "../../../Context/TablePageContext";
import strings from "../../../localization";
import {
    deleteOrganisationUserContract, generateOrganisationUserContractParts,
    getOrganisationUserContractList
} from "../../../Services/HR/OrganisationUserContractService";
import PageState from "../../../Constants/Base/PageState";
import AddOrganisationUserContract from "./AddOrganisationUserContract";
import EditOrganisationUserContract from "./EditOrganisationUserContract";
import {useNavigate, useSearchParams} from "react-router-dom";
import ReferenceType from "../../../Constants/DocumentManagement/ReferenceType";
import {
    getOrganisationUserContractTypes,
    getOrganisationUserContractTypeString
} from "../../../Constants/HR/OrganisationUserContractType";
import {renderColumnNumber} from "../../../Components/DataGrid/ValueCellRender";
import {addressBook} from 'react-icons-kit/icomoon/addressBook'
import {Icon} from "react-icons-kit";
import AppPermissions from "../../../Constants/Permissions/Permissions";
import {UpgradeOutlined} from "@mui/icons-material";
import SnackbarContext from "../../../Context/SnackbarContext";
import {fetchOptions} from '../../../Services/Base/FilterOptionsService';
import { getEmployees, getEmployeeByUser } from "../../../Services/User/UserService";
import Tag from "../../../Components/Forms/Pages/Document/Tag/Tag";

const tableDescription = [
    {
        field: 'name',
        headerName: strings.pages.hr.organisationUserContract.organisationUserContractList.name,
        width: 150
    },
    {
        field: 'type', headerName: strings.pages.hr.organisationUserContract.organisationUserContractList.type,
        valueFormatter: (params) => formatColumnConstant(params, getOrganisationUserContractTypeString),
        width: 150
    },
    {
        field: 'startDate',
        headerName: strings.pages.hr.organisationUserContract.organisationUserContractList.startDate,
        width: 100,
        valueFormatter: (params) => formatColumnDate(params)
    },
    {
        field: 'endDate', headerName: strings.pages.hr.organisationUserContract.organisationUserContractList.endDate,
        valueFormatter: (params) => formatColumnDate(params),
        width: 100,
    },
    {
        field: 'net',
        headerName: strings.pages.hr.organisationUserContract.organisationUserContractList.net,
        width: 120,
        renderCell: (params) => renderColumnNumber(params)
    },
    {
        field: 'gross',
        headerName: strings.pages.hr.organisationUserContract.organisationUserContractList.gross,
        width: 120,
        renderCell: (params) => renderColumnNumber(params)
    },
    {
        field: 'currency', headerName: strings.pages.hr.organisationUserContract.organisationUserContractList.currency,
        valueFormatter: (params) => formatColumnObject(params, 'code'),
        width: 90
    },
    {
        field: 'user', headerName: strings.pages.hr.organisationUserContract.organisationUserContractList.user,
        valueFormatter: (params) => formatColumnObject(params, 'fullName'),
        width: 200,
    }
];


const OrganisationUserContractList = (props) => {

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [updatedFilter, setUpdatedFilter] = useState(!Boolean(searchParams.toString()));
    const [columns, setColumns] = useState([]);
    const [pageState, setPageState] = useState(PageState.View);
    const [showSnackbar, setShowSnackbar] = useState(false);
    const [messageSnackbar, setMessageSnackbar] = useState('');
    const [alertType, setAlertType] = useState('success');
    const [filter, setFilter] = useState(FilterDefaults);
    const [tableData, setTableData] = useState(TableDataDefaults);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [selectionModel, setSelectionModel] = useState([]);
    const [selectedItems, setSelectedItems] = useState(null);
    const [tablePageOptions, setTablePageOptions] = useState({
        ...TablePageOptionDefaults,
        showYearFilter: true,
        showMonthFilter: true
    });
    const referenceType = ReferenceType.ORGANISATION_USER_CONTRACT;
    const [filterOptions, setFilterOptions] = useState({});
    const permissionGroup = AppPermissions.ORGANISATION_USER_CONTRACT.GROUP;
    const { showMessage } = useContext(SnackbarContext);

    const value = {
        tablePageOptions, setTablePageOptions,
        selectionModel, setSelectionModel,
        selectedItems, setSelectedItems,
        filter, setFilter, pageState, setPageState, showSnackbar, setShowSnackbar,
        messageSnackbar, setMessageSnackbar, alertType, setAlertType, showDeleteDialog, setShowDeleteDialog,
        navigate, updatedFilter, setUpdatedFilter
    }

    const additionalMenuItems = [
        {
            handleClick: (id, item) => handleContractParts(item),
            text: strings.pages.hr.organisationUserContract.organisationUserContractList.contractParts,
            src: '/images/table-page/contract-parts.svg'
        },
        {
            handleClick: (id, item) => handleGenerateParts(item),
            text: strings.pages.hr.organisationUserContract.organisationUserContractList.generateParts,
            src: '/images/table-page/contract-generate-parts.svg'
        },
    ]

    useEffect(() => {
        dispatch(changeBreadcrumbs({
            title: strings.pages.hr.organisationUserContract.organisationUserContractList.pageTitle,
            hierarchy:[
                {label: strings.navigation.managmentTag},
                {label: Modules.HR},
                {label: strings.pages.hr.organisationUserContract.organisationUserContractList.pageTitle},
            ],        
        }));
        dispatch(changePageSizeState(PageSizeState.SHORT));

        setColumns([...tableDescription]);

        fetchFilters();
        

        return () => {
            setTableData({})
        }
    }, [])

    useEffect(() => {
        if(!updatedFilter){
            return;
        }

        fetch();
    }, [filter, updatedFilter]);

    const fetchEmployees = async (term, filterFetch) => {
        return fetchOptions('employee', searchParams, getEmployees, getEmployeeByUser, setFilterOptions, term, filterFetch, 'user');
    }

    const fetchFilters = async () => {
        const employees = await fetchEmployees();
        
        setFilterOptions({
            employee: employees,
            type: getOrganisationUserContractTypes(),
            fetched: 1,
        });
    }

    const filters = [
        {
            name: 'type',
            nameKey: 'name',
            valueKey: 'id',
            optionsName: 'type',
            label: strings.pages.hr.organisationUserContract.organisationUserContractList.type,
            showSelect: true
        },
        {
            name: 'employee',
            nameKey: 'fullName',
            valueKey: 'id',
            optionsName: 'employee',
            label: strings.pages.hr.organisationUserContract.organisationUserContractList.employee,
            onChange: (term) => fetchEmployees(term, true),
        },
    ];

    const onCancel = () => {
        setPageState(PageState.View);
    }

    const onFinish = () => {
        fetch();
        setPageState(PageState.View);
    }

    const fetch = () => {
        setTableData({
            ...tableData,
            loading: true
        });

        getOrganisationUserContractList({
            ...filter
        }).then(response => {

            if (!response || !response.ok) {
                return;
            }

            setTableData({
                loading: false,
                data: response.data.result,
                total: response.data.total
            });
        })
    }

    const handleContractParts = (organisationUserContract) => {
        navigate(`/organisation-user-contract/${organisationUserContract.id}/parts`)
    }

    const handleGenerateParts = (organisationUserContract) => {
        generateOrganisationUserContractParts({...organisationUserContract}).then(response => {
            if (!response.ok) {
                showMessage(strings.error.OrganisationUserContractHandler[response.response.data.message], 'error');
                return;
            }
            handleContractParts(response.data.entity)
        });
    }

    return <TablePageContext.Provider value={value}>
        <TablePage onFinish={() => onFinish()} deleteItem={deleteOrganisationUserContract} tableDescription={columns}
                   tableData={tableData} filter={filter} filters={filters} filterOptions={filterOptions}
                   additionalMenuItems={additionalMenuItems}
                   referenceType={referenceType}
                   permissionGroup={permissionGroup}
                   tagPage={<Tag referenceType={ referenceType } data={ selectedItems } /> }
                   editPage={<EditOrganisationUserContract data={selectedItems}
                                                           onCancel={() => onCancel()} onFinish={() => onFinish()}/>}
                   addPage={<AddOrganisationUserContract onCancel={() => onCancel()} onFinish={() => onFinish()}/>}
                   addButtonText={strings.components.tablePage.addButton.employeeContract}
        />
    </TablePageContext.Provider>
}

export default OrganisationUserContractList;
